import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FaCheck } from "react-icons/fa";
import ProgressBar from "../common/ProgressBar";
import FeedMatchFusedLine from "../feed/FeedMatchFusedLine";
import useQuickComparison from "../../hooks/useQuickComparison";
import useEnrichedMatchProducts from "../../hooks/useEnrichedMatchProducts";
import {
  buildComparisonCards,
  getTopMatchIndex,
} from "../../utils/quickComparisonUtils";
import { resolveSearchParamsForProducts } from "../../utils/resolveComparisonAiSession";
import { buildMatchPriceSummary, buildMatchTradeOff } from "../../utils/matchTradeoffUtils";
import {
  buildDecisionTradeoff,
  buildPickReason,
  decisionPickRole,
} from "../../utils/decisionPickCopy";
import {
  buildMatchTagLabels,
  fallbackMatchExplanation,
  normalizeMatchExplanation,
  sanitizeMatchExplanationForProduct,
} from "../../utils/matchDisplayUtils";
import { getDecisionStageLabel } from "../../utils/activeDecisionUtils";
import getImageUrl, { resolveProductImagePath } from "../../utils/imageUtils";
import useShortlistCoach from "../../hooks/useShortlistCoach";
import useDecisionChoice from "../../hooks/useDecisionChoice";
import useDecisionCohort from "../../hooks/useDecisionCohort";
import ShortlistCoachPanel from "./ShortlistCoachPanel";
import DecisionReceipt from "./DecisionReceipt";
import { buildProductLinkLabel } from "../../utils/matchCardA11y";
import styles from "../../styles/components/home/HelpMeDecideSection.module.css";

const PENDING_PRICE_WATCH_KEY = "simpary_pending_price_watch";

function categoryIdOf(product) {
  const category = product?.category;
  if (!category) return "";
  if (typeof category === "object") return String(category._id || category.id || "");
  return String(category);
}

function followUpIsOpen(choice, now = Date.now()) {
  if (!choice?.followUpAt || choice.followUp?.closed) return false;
  const answer = choice.followUp?.answer;
  if (answer === "bought_it" || answer === "bought_other") return false;
  return new Date(choice.followUpAt).getTime() <= now;
}

const getProductPath = (product) => {
  const catId = product?.category?._id || product?.category;
  if (catId) return `/category/${catId}/product/${product._id}`;
  return `/product/${product._id}`;
};

const buildMatchTags = (product, searchParams) => {
  const labels = buildMatchTagLabels(searchParams);
  const tags = labels.map((label) => ({ label, variant: "purple" }));

  if (
    product?.price != null &&
    searchParams?.budget?.max != null &&
    tags.length < 3 &&
    !labels.some((label) => label.startsWith("under $"))
  ) {
    tags.push({ label: "In budget", variant: "mint" });
  }

  return tags.slice(0, 3);
};

const MergedShortlistCard = ({
  card,
  product,
  siblings = [],
  searchParams,
  matchScoreRevision = 0,
  topPickProduct = null,
  isTopMatch,
  isValueWinner,
  showValuePick,
  productPath,
  showMetrics = true,
  showPick = false,
  pickReady = false,
  picking = false,
  onPick,
  cohortSentence = "",
}) => {
  const rawReason =
    product.matchExplanation && typeof product.matchExplanation === "string"
      ? sanitizeMatchExplanationForProduct(
          product.matchExplanation,
          product,
          siblings,
          searchParams,
        )
      : null;
  // Only replace contaminated blurbs — don't invent copy when none was provided.
  const hadExplanation =
    typeof product.matchExplanation === "string" &&
    Boolean(product.matchExplanation.trim());
  const groundedFallback =
    hadExplanation && !rawReason
      ? fallbackMatchExplanation(product, searchParams)
      : null;
  const reason = normalizeMatchExplanation(
    rawReason || groundedFallback,
    searchParams,
  );
  const tags = buildMatchTags(product, searchParams);
  const tradeOff =
    topPickProduct && String(topPickProduct._id) !== String(product._id)
      ? buildMatchTradeOff(product, topPickProduct)
      : null;

  const cardRank = card?.rank ?? product.rank ?? null;
  // Catalog product is source of truth for identity — never let comparison
  // card metadata drift onto a different SKU's explanation.
  const displayName = product.name ?? card?.name;
  const cardPrice =
    product.price != null
      ? `$${Number(product.price).toLocaleString()}`
      : card?.price ?? "—";
  const productLinkLabel = buildProductLinkLabel(product, {
    priceLabel: cardPrice !== "—" ? cardPrice : null,
    rank: cardRank,
  });

  return (
    <article
      className={`${styles.card} ${
        isValueWinner && showValuePick ? styles.cardWinner : ""
      } ${isTopMatch ? styles.cardTopMatch : ""}`.trim()}
    >
      <div className={styles.cardPickMeta}>
        <span className={styles.cardRank}>Pick #{cardRank ?? "—"}</span>
        {isTopMatch && (
          <span className={styles.topMatchCardBadge}>
            {searchParams?.productConstraint?.unmatched
              ? "Closest alternative"
              : "Top match"}
          </span>
        )}
        {isValueWinner && showValuePick && (
          <span className={styles.valueCardBadge}>Best value</span>
        )}
      </div>

      <Link
        to={productPath}
        className={styles.cardVisual}
        aria-label={productLinkLabel}
      >
        <div className={styles.cardImageWrap}>
          <img
            src={getImageUrl(resolveProductImagePath(product))}
            alt=""
            className={styles.cardImage}
            loading="lazy"
          />
        </div>
      </Link>

      <div className={styles.cardDetails}>
        <Link to={productPath} className={styles.cardTitleLink}>
          <h3 className={styles.cardName}>{displayName}</h3>
        </Link>
        {reason && (
          <p className={styles.matchWhy}>
            <span className={styles.matchWhyLabel}>Why it fits:</span>{" "}
            {reason}
          </p>
        )}

        <FeedMatchFusedLine
          product={product}
          wizardRevision={matchScoreRevision}
          forceAligned
          className={styles.matchFusedLine}
        />

        {tradeOff && <p className={styles.tradeOff}>{tradeOff}</p>}
        {cohortSentence ? (
          <p className={styles.cohortNote}>{cohortSentence}</p>
        ) : null}

        {showMetrics && card?.metrics?.length > 0 && (
          <ul className={styles.metricList}>
            {card.metrics.map((metric) => (
              <li
                key={metric.key}
                className={`${styles.metricItem} ${
                  metric.isBest ? styles.metricBest : ""
                }`}
              >
                <div className={styles.metricMeta}>
                  <span className={styles.metricLabel}>{metric.label}</span>
                  <span className={styles.metricValue}>
                    {metric.value}
                    {metric.isBest && (
                      <FaCheck className={styles.winIcon} aria-hidden="true" />
                    )}
                  </span>
                </div>
                <div className={styles.metricTrack}>
                  <ProgressBar
                    className={`${styles.metricFill} ${
                      metric.isBest ? styles.metricFillBest : ""
                    }`}
                    percentage={metric.barPercent}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}

        {tags.length > 0 && (
          <div className={styles.tagRow}>
            {tags.map((tag) => (
              <span
                key={tag.label}
                className={`${styles.tag} ${
                  tag.variant === "mint" ? styles.tagMint : styles.tagPurple
                }`}
              >
                {tag.label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className={styles.cardAside}>
        <p className={styles.cardPrice} aria-hidden="true">
          {cardPrice}
        </p>
        {showPick && (
          <button
            type="button"
            className={styles.pickBtn}
            onClick={pickReady && !picking ? onPick : undefined}
            disabled={!pickReady || picking}
            aria-disabled={!pickReady || picking}
            aria-busy={!pickReady}
          >
            {!pickReady && (
              <span className={styles.pickSpinner} aria-hidden="true" />
            )}
            This is the one
          </button>
        )}
        <Link to={productPath} className={styles.viewDetailsBtn}>
          See details <span aria-hidden="true">→</span>
        </Link>
      </div>
    </article>
  );
};

const HelpMeDecideSection = ({
  products = [],
  searchParams = null,
  matchScoreRevision = 0,
  activeDecision = null,
  choice = null,
  sessionId = null,
  ownerUserId = null,
  isGuest = false,
}) => {
  const navigate = useNavigate();
  const [pageSearchParams] = useSearchParams();
  const [revising, setRevising] = useState(false);
  const decisionSectionRef = useRef(null);
  const pendingDecisionScrollRef = useRef(false);
  const {
    setChoice,
    clearChoice,
    shareChoice,
    watchPrice,
    stopWatch,
    answerFollowUp,
    saving,
    error: choiceError,
    shareNotice,
  } = useDecisionChoice({ sessionId, ownerUserId, isGuest });
  const enrichedProducts = useEnrichedMatchProducts(products);
  const resolvedSearchParams = useMemo(
    () => resolveSearchParamsForProducts(enrichedProducts, searchParams),
    [enrichedProducts, searchParams],
  );
  const {
    table,
    valuePickIndex,
    topMatchIndex,
    isValuePick,
    loading,
    aiLoading,
    aiSettled,
  } = useQuickComparison({
    products: enrichedProducts,
    searchParams: resolvedSearchParams,
  });

  const resolvedTopMatchIndex =
    topMatchIndex >= 0 ? topMatchIndex : getTopMatchIndex(enrichedProducts);
  const topMatch =
    enrichedProducts[resolvedTopMatchIndex] || enrichedProducts[0];
  const valueWinner =
    enrichedProducts[valuePickIndex >= 0 ? valuePickIndex : 0] ||
    enrichedProducts[0];
  const resolvedIsValuePick =
    isValuePick ||
    (enrichedProducts.length >= 2 &&
      resolvedTopMatchIndex >= 0 &&
      valuePickIndex >= 0 &&
      valuePickIndex !== resolvedTopMatchIndex);

  const {
    coaching,
    loading: coachLoading,
    settled: coachSettled,
  } = useShortlistCoach({
    products: enrichedProducts,
    searchParams: resolvedSearchParams,
    comparisonTable: table,
    topMatchProductId: topMatch?._id,
    valuePickProductId: valueWinner?._id,
    // Wait until quick-comparison has finished once — avoids coach firing
    // before compare starts and again when the AI table updates.
    enabled:
      enrichedProducts.length >= 2 &&
      Boolean(table?.rows?.length) &&
      aiSettled &&
      !aiLoading,
  });

  const cards = useMemo(
    () => buildComparisonCards({ table, products: enrichedProducts }),
    [table, enrichedProducts],
  );

  useEffect(() => {
    if (isGuest || !sessionId) return undefined;
    const pending = sessionStorage.getItem(PENDING_PRICE_WATCH_KEY);
    if (pending !== sessionId) return undefined;
    sessionStorage.removeItem(PENDING_PRICE_WATCH_KEY);
    watchPrice();
    return undefined;
  }, [isGuest, sessionId, watchPrice]);

  const receiptVisible =
    Boolean(choice?.productId) && !revising && enrichedProducts.length > 0;

  useEffect(() => {
    if (!pendingDecisionScrollRef.current || !receiptVisible) return undefined;
    pendingDecisionScrollRef.current = false;
    decisionSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    return undefined;
  }, [receiptVisible]);

  const cohortStats = useDecisionCohort({
    productIds: enrichedProducts.map((product) => product._id),
    categoryId: categoryIdOf(enrichedProducts[0]),
    budgetMax: resolvedSearchParams?.budget?.max,
    useCase: resolvedSearchParams?.useCase,
  });

  if (!enrichedProducts.length) return null;

  const canCompare = enrichedProducts.length >= 2;
  const topPickProduct = enrichedProducts[0];
  const decisionLabel = getDecisionStageLabel(activeDecision?.stage);
  const priceSummary = buildMatchPriceSummary(enrichedProducts);
  const shortlistOnScreen = canCompare && Boolean(table?.rows?.length);
  const pickReady = Boolean(coachSettled);
  const chosenProductId = choice?.productId ? String(choice.productId) : null;
  const showReceipt = Boolean(chosenProductId) && !revising;
  const receiptProduct =
    enrichedProducts.find((product) => String(product._id) === chosenProductId) ||
    choice?.product ||
    null;

  const pickContext = (product) => {
    const productId = String(product?._id || product?.id || "");
    return {
      role: decisionPickRole(productId, {
        topProduct: topMatch,
        valueProduct: valueWinner,
      }),
      otherNames: enrichedProducts
        .filter((item) => String(item._id) !== productId)
        .map((item) => item.name),
    };
  };

  const handlePick = async (product) => {
    const productId = String(product._id);
    const advice = coaching?.guidance?.find(
      (item) => String(item.productId) === productId,
    )?.advice;
    const { role, otherNames } = pickContext(product);
    const reason = buildPickReason({
      product,
      advice: String(advice || coaching?.headline || "").trim(),
      searchParams: resolvedSearchParams,
      otherNames,
      role,
    });
    const tradeoff = buildDecisionTradeoff(product, topMatch, table);
    const saved = await setChoice({ productId, tradeoff, reason });
    if (saved) {
      pendingDecisionScrollRef.current = true;
      setRevising(false);
    }
  };

  const handleClear = async () => {
    const cleared = await clearChoice();
    if (cleared) setRevising(false);
  };

  const handleWatch = () => {
    if (isGuest) {
      if (sessionId) sessionStorage.setItem(PENDING_PRICE_WATCH_KEY, sessionId);
      navigate("/login", {
        state: {
          from: { pathname: "/", hash: "#home-match-results" },
          action: "watch the price",
        },
      });
      return;
    }
    watchPrice();
  };

  // Decision-stage-aware nudge: when the shopper model/session signals the
  // user is actively comparing, make the compare CTA a bit more prominent
  // instead of treating every stage identically.
  const isComparingStage = activeDecision?.stage === "comparing";
  const compareAllLabel = isComparingStage
    ? `Compare all ${enrichedProducts.length} now`
    : enrichedProducts.length === 3
      ? "Compare all 3"
      : `Compare all ${enrichedProducts.length}`;

  const unmatchedMessage = resolvedSearchParams?.productConstraint?.unmatched
    ? resolvedSearchParams.productConstraint.message
    : "";

  const displayCards = canCompare
    ? cards
    : enrichedProducts.map((product, index) => ({
        id: String(product._id),
        rank: product.rank ?? index + 1,
        name: product.name,
        price:
          product.price != null
            ? `$${Number(product.price).toLocaleString()}`
            : "—",
        metrics: [],
      }));

  return (
    <section
      ref={decisionSectionRef}
      className={styles.section}
      aria-labelledby="home-shortlist-title"
      id="home-match-results"
    >
      <header className={styles.header}>
        <h2 id="home-shortlist-title" className={styles.title}>
          <span className={styles.titleMark} aria-hidden="true">
            <FaCheck />
          </span>
          {showReceipt
            ? "Your decision"
            : unmatchedMessage || "Best matches for you"}
        </h2>
        {decisionLabel && !showReceipt && (
          <p className={styles.decisionContext}>{decisionLabel}</p>
        )}
        {priceSummary && !showReceipt && (
          <p className={styles.priceSummary}>{priceSummary}</p>
        )}
        {canCompare && !showReceipt && !unmatchedMessage && (
          <p className={styles.subtitle}>
            {resolvedIsValuePick
              ? `Your top match by score is ${topMatch.name}. Compare price, specs, and fit across your shortlist below.`
              : "See how your shortlist compares on price, specs, and match score."}
          </p>
        )}
      </header>

      {canCompare && !showReceipt && (
        <ShortlistCoachPanel
          coaching={coaching}
          products={enrichedProducts}
          loading={coachLoading || (aiLoading && !coaching?.headline)}
        />
      )}

      {showReceipt && receiptProduct ? (
        <DecisionReceipt
          product={{
            ...receiptProduct,
            price: receiptProduct.price ?? choice?.product?.price,
          }}
          reason={choice?.reason || ""}
          tradeoff={buildDecisionTradeoff(receiptProduct, topMatch, table)}
          searchParams={resolvedSearchParams}
          {...pickContext(receiptProduct)}
          sessionId={sessionId}
          loadOffers
          onChangePick={() => setRevising(true)}
          onClear={handleClear}
          onShare={shareChoice}
          priceWatch={choice?.priceWatch || null}
          askFollowUp={
            !isGuest &&
            choice?.followUp?.answer !== "bought_it" &&
            choice?.followUp?.answer !== "bought_other" &&
            (followUpIsOpen(choice) ||
              (pageSearchParams.get("followUp") === "1" &&
                !choice?.followUp?.answer))
          }
          followUpAnswer={choice?.followUp?.answer || ""}
          reaskCount={choice?.followUp?.reaskCount || 0}
          alternatives={enrichedProducts.filter(
            (product) => String(product._id) !== String(receiptProduct._id),
          )}
          cohortSentence={
            cohortStats[String(receiptProduct._id || receiptProduct.id)]?.sentence || ""
          }
          onWatch={handleWatch}
          onStopWatch={stopWatch}
          onAnswer={answerFollowUp}
          saving={saving}
          shareNotice={shareNotice}
          error={choiceError}
        />
      ) : loading && !displayCards.length ? (
        <p className={styles.loading}>
          {aiLoading ? "Personalizing comparison…" : "Loading product details…"}
        </p>
      ) : displayCards.length > 0 ? (
        <>
          <div className={styles.cardGrid}>
            {displayCards.map((card, index) => {
              const product =
                enrichedProducts.find(
                  (p) => String(p._id) === String(card.id),
                ) || enrichedProducts[index];
              if (!product) return null;

              const productId = String(product._id || card.id);
              const isTopMatch = String(topMatch?._id) === productId;
              const isValueWinner = String(valueWinner?._id) === productId;

              return (
                <MergedShortlistCard
                  key={card.id}
                  card={card}
                  product={product}
                  siblings={enrichedProducts}
                  searchParams={searchParams}
                  matchScoreRevision={matchScoreRevision}
                  topPickProduct={topPickProduct}
                  isTopMatch={isTopMatch}
                  isValueWinner={isValueWinner}
                  showValuePick={resolvedIsValuePick}
                  productPath={getProductPath(product)}
                  showMetrics={canCompare}
                  showPick={shortlistOnScreen}
                  pickReady={pickReady}
                  picking={saving}
                  cohortSentence={cohortStats[productId]?.sentence || ""}
                  onPick={() => handlePick(product)}
                />
              );
            })}
          </div>

          {canCompare && (
            <footer className={styles.actions}>
              {revising && (
                <button
                  type="button"
                  className={styles.textBtn}
                  onClick={handleClear}
                  disabled={saving}
                >
                  Clear decision
                </button>
              )}
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() =>
                  navigate("/compare", {
                    state: {
                      products: enrichedProducts,
                      searchParams: resolvedSearchParams,
                    },
                  })
                }
              >
                {compareAllLabel}
              </button>
              {choiceError && !showReceipt && (
                <p className={styles.choiceError} role="alert">
                  {choiceError}
                </p>
              )}
            </footer>
          )}
        </>
      ) : null}
    </section>
  );
};

export default HelpMeDecideSection;
